import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Row, Col, Button, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import DatePicker from "react-datepicker";
import { BsCalendar } from 'react-icons/bs';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "react-datepicker/dist/react-datepicker.css";
import '../styles/main.css';
import { apiGet } from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const UserFrequencyForm = () => {
  const [taskCoordinators, setTaskCoordinators] = useState([]);
  const [taskCoordinator, setTaskCoordinator] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [userFrequencies, setUserFrequencies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);

  const startDateRef = useRef();
  const endDateRef = useRef();

  const columns = [
    { name: 'Username', selector: row => row.userName, sortable: true ,center: true,
       
       style: {
      borderRight: '1px solid #dee2e6',
      textAlign:"center"
    },
    },
    { name: 'Login Count',
       selector: row => row.loginCount, 
       sortable: true ,
        center: true,
     cell: row => (
    <div className="followed-by-cell">
      {row.loginCount}
    </div>
  ),
       style: {
      borderRight: '1px solid #dee2e6',
      textAlign:"center"
    },
    },
    { name: 'Update Count', selector: row => row.taskUpdateCount, sortable: true, center: true,
     
       style: {
    
      textAlign:"center"
    },
     }
  ];

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#2c3e50',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '14px'
      }
    },
    rows: {
      style: { 
        fontSize: '14px',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      }
    }
  };

  const fetchTaskCoordinators = useCallback(async () => {
    try {
      const response = await apiGet('/User/GetAllUsers');
      if (!response?.isSuccess) throw new Error('Failed to fetch task coordinators');
      const coordinators = response.data
        .filter(user => user.roleName?.toLowerCase() === 'taskcoordinator')
        .map(user => ({ value: user.userID, label: user.username }));
      setTaskCoordinators(coordinators);
    } catch (err) {
      console.error(err);
      setError('Failed to load task coordinators');
    }
  }, []);

  const fetchUserFrequencies = useCallback(async () => {
    setIsSearching(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      if (taskCoordinator) params.append('taskCoordinatorId', taskCoordinator.value);

      const [usersRes, freqRes] = await Promise.all([
        apiGet('/User/GetAllUsers'),
        apiGet(`/User/GetAllFrequencies?${params}`)
      ]);

      if (!usersRes?.isSuccess || !freqRes?.isSuccess) {
        throw new Error('Failed to fetch data');
      }

      const data = freqRes.data.map(freq => {
        const user = usersRes.data.find(u => u.userID === freq.userID);
        return {
          ...freq,
          userName: user ? user.username : `User ${freq.userID}`
        };
      });

      setUserFrequencies(data);
      setShowTable(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch frequency data');
    } finally {
      setIsSearching(false);
    }
  }, [startDate, endDate, taskCoordinator]);

  useEffect(() => {
    fetchTaskCoordinators();
  }, [fetchTaskCoordinators]);

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      setError('End date cannot be before start date');
      setShowTable(false);
      return;
    }
    fetchUserFrequencies();
  };

  const handleClear = () => {
    setTaskCoordinator(null);
    setStartDate(null);
    setEndDate(null);
    setUserFrequencies([]);
    setSelectedUserData(null);
    setShowTable(false);
    setError(null);
  };

  const exportToExcel = () => {
    const sheetData = userFrequencies.map(({ userName, loginCount, taskUpdateCount }) => ({
      Username: userName,
      'Login Count': loginCount,
      'Update Count': taskUpdateCount
    }));

    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'User Frequency');
    XLSX.writeFile(wb, 'UserFrequency.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'A4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 30;
    const title = "User Frequency Report";
    const date = new Date().toLocaleDateString('en-GB');

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, 40);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const dateWidth = doc.getTextWidth(date);
    doc.text(date, pageWidth - margin - dateWidth, 40);

    autoTable(doc, {
      startY: 60,
      margin: { left: margin, right: margin },
      head: [['Username', 'Login Count', 'Update Count']],
      body: userFrequencies.map(row => [row.userName, row.loginCount, row.taskUpdateCount]),
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      styles: { fontSize: 11, halign: 'center', cellPadding: 6 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableLineColor: [220, 220, 220],
      tableLineWidth: 0.2
    });

    doc.save('UserFrequency.pdf');
  };

  const handleRowClicked = row => {
    setSelectedUserData(row);
  };

  return (
    <div className="container mt-4 mb-4">
       <div className="tabs-section-wrapper mb-2">
        <div className='row'>
 <div className="d-flex justify-content-lg-center d-block tabs-section gap-3 mb-2">
        <Link to="/dashboard/reports"><Button variant="light" className="btn-tab  btn-btn-sechover">Task Detail</Button></Link>
        <Link to="/dashboard/UserLoginHistory"><Button variant="light" className="btn-tab btn-btn-sechover">User Login History</Button></Link>
        <Link to="/dashboard/UserTaskUpdate"><Button variant="light" className="btn-tab btn-btn-sechover">User Task Update</Button></Link>
        <Link to="/dashboard/UserFrequency"><Button variant="btn-primary" className="btn-tab  btn-btn-primary">User Frequency</Button></Link>
          <Link to="/dashboard/voiceSearch"><Button variant="light" className="btn-tab">VoiceSearch</Button></Link>
          </div>        
      </div>
      </div>

      <div className="row">
        {/* Main content area - takes full width when no graph, 8 columns when graph is shown */}
        <div className={selectedUserData ? "col-md-8" : "col-md-12 px-0 mx-0"}>
          <div className='custombox'>
            <div className='rounded p-4 mb-4'>
              <div className="text-center customheader">
                <h5 className="mb-0">User Frequency</h5>
              </div>

              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

              <Form>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>Task Coordinator</Form.Label>
                    <Select
                      options={taskCoordinators}
                      value={taskCoordinator}
                      onChange={setTaskCoordinator}
                      isClearable
                      placeholder="Select Task Coordinator"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label  className='add-departmentbtn'>Date Range (From)</Form.Label>
                    <InputGroup>
                      <DatePicker
                        selected={startDate}
                        onChange={setStartDate}
                        ref={startDateRef}
                        className="form-control"
                        maxDate={new Date()}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="Start Date"
                      />
                      <InputGroup.Text onClick={() => startDateRef.current.setOpen(true)}><BsCalendar /></InputGroup.Text>
                    </InputGroup>
                  </Col>
                  <Col md={4}>
                    <Form.Label  className='add-departmentbtn'>Date Range (To)</Form.Label>
                    <InputGroup>
                      <DatePicker
                        selected={endDate}
                        onChange={setEndDate}
                        ref={endDateRef}
                        className="form-control"
                        minDate={startDate}
                        maxDate={new Date()}
                     dateFormat="dd-MM-yyyy"
                        placeholderText="End Date"
                      />
                      <InputGroup.Text onClick={() => endDateRef.current.setOpen(true)}><BsCalendar /></InputGroup.Text>
                    </InputGroup>
                  </Col>
                </Row>

                <div className="text-center mt-4">
                  <Button variant="btn-primary" className="px-4 me-3 btn-fontweight" onClick={handleSearch} >
                   Search
                  </Button>
                  <Button variant="secondary" className="px-4 btn-fontweight" onClick={handleClear}>Clear</Button>
                </div>
              </Form>
            </div>
          </div>
 
        
        </div>
          {selectedUserData && (
          <div className="col-md-4 px-0 mx-0">
         <div>
              <h5 className="text-center  graph-heading mb-3" >Activity: {selectedUserData.userName}</h5>
              <div style={{ height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[selectedUserData]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="userName" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="loginCount"    barSize={30}  fill="#8884d8" name="Login Count" />
                    <Bar dataKey="taskUpdateCount"    barSize={30}  fill="#82ca9d" name="Update Count" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-center ">
                <Button 
                 className='custompdf-btn btn-fontweight '
                  size="sm" 
                  onClick={() => setSelectedUserData(null)}
                >
                  Close Graph
                </Button>
              </div>
            </div>
          </div>
        )}

        {showTable && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <Button className="me-2 btn-fontweight custompdf-btn"  onClick={exportToPDF} disabled={userFrequencies.length === 0}>
                    Export to PDF
                  </Button>
                  <Button  className='btn-fontweight customexport-btn' onClick={exportToExcel} disabled={userFrequencies.length === 0}>
                    Export to Excel
                  </Button>
                </div>
              </div>

              <div className="card shadow-sm rounded-4 py-4 px-0  border-0 bg-white mb-custom">
                <DataTable
                  columns={columns}
          
                         data={userFrequencies.map((dept, idx) => ({
                          ...dept,
                          _rowIndex: idx, 
                             }))}
                  pagination
                  progressPending={isLoading || isSearching}
                  progressComponent={<Spinner animation="border" />}
                  noDataComponent={<div className="text-center py-4">No frequency data available</div>}
                  onRowClicked={handleRowClicked}
                  striped

                  className="p-datatable-striped customuser-data-table customarchive-data-table dashboard-table"
      customStyles={{
              table: {
                style: {
              fontWeight: '600',
      fontSize: '14px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                    textAlign: 'center',

                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                    textAlign: 'center',
                  color: "white",
                  textTransform: "uppercase",
                   textAlign: columns.name === "Issue/Action/Decision" ? "center" : "left",
                },
              },
           
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
      conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]} 
       
        
                />
              </div>
            </div>
          )}
     
      </div>
    </div>
  );
};

export default UserFrequencyForm;